import { useCallback, useMemo, useState } from "react";
import type { StudentTestContent } from "p6m-p6u";

interface UseExerciseNavigationParams {
    content: StudentTestContent["content"];
}

const useExerciseNavigation = (params: UseExerciseNavigationParams) => {
    const [currentExerciseId, setCurrentExerciseId] = useState("e1");
    const [showOverview, setShowOverview] = useState(false);

    const content = params.content;

    const currentExerciseIndex = useMemo(
        () => content?.findIndex((e) => e.id === currentExerciseId) ?? -1,
        [content, currentExerciseId]
    );

    const currentPage = currentExerciseIndex + 1;
    const totalPages = content?.length ?? 1;

    const navigateToPage = useCallback(
        (offset: number) => {
            if (!content) return;
            const targetIndex = currentExerciseIndex + offset;
            if (targetIndex >= 0 && targetIndex < content.length) {
                const targetId = content[targetIndex].id;
                if (targetId) setCurrentExerciseId(targetId);
            }
        },
        [content, currentExerciseIndex]
    );

    const handlePreviousPage = useCallback(() => navigateToPage(-1), [navigateToPage]);

    const handleNextPage = useCallback(() => {
        if (!content) return;
        if (currentExerciseIndex < content.length - 1) {
            navigateToPage(1);
        } else {
            setShowOverview(true);
        }
    }, [content, currentExerciseIndex, navigateToPage]);

    const handleOverviewClick = () => setShowOverview(true);
    const handleCloseOverview = () => setShowOverview(false);

    const handleNavigateToExercise = useCallback((exerciseId: string) => {
        setCurrentExerciseId(exerciseId);
        setShowOverview(false);
    }, []);

    return {
        currentExerciseId,
        setCurrentExerciseId,
        currentExerciseIndex,
        currentPage,
        totalPages,
        showOverview,
        setShowOverview,
        handlePreviousPage,
        handleNextPage,
        handleOverviewClick,
        handleCloseOverview,
        handleNavigateToExercise,
    };
};

export default useExerciseNavigation;
